import { useQuery } from '@tanstack/react-query';

import { COHORTS_PATH } from '@verantos-dev/core-constants';
import type { Cohort, CohortDefinitionStatus } from '@verantos-dev/types-cohorts';
import type { AssetStatus } from '@verantos-dev/types-projects';

import { authFetch } from './fetchers';
import { parseOrFallback } from './parseOrFallback';
import { cohortQueryKeys } from './queryKeys';
import { cohortSchema, objectResponse, type CohortFromApi } from './schemas';

export interface CohortProfileView {
  cohortId: string;
  cohortName: string;
  cohortDescription: string;
  latestStatus: CohortDefinitionStatus;
  /** Backend-derived unified asset status (Draft / Processing / Ready / Error)
   *  from ``cohort_to_asset_status``. Optional: the cache-prime snapshot
   *  (``projectCohortToProfileView``) omits it; the authoritative fetch fills it. */
  assetStatus?: AssetStatus | null;
  latestCohortVersionId: string;
  createdByUserId: string;
  createdByFirstName: string;
  createdByLastName: string;
  modifiedByUserId: string;
  modifiedByTransactionDate: string;
  latestSubjectCount: number;
  latestDatasetReleaseId?: string;
  latestDatasetReleaseDisplayName?: string;
}

/**
 * Project the legacy `Cohort` list-row envelope into the
 * {@link CohortProfileView} shape so a snapshot from the artifacts list
 * can prime the TanStack profile cache without waiting for the network.
 */
export const projectCohortToProfileView = (snapshot: Cohort): CohortProfileView => ({
  cohortId: snapshot.cohortId,
  cohortName: snapshot.cohortName,
  cohortDescription: snapshot.cohortDescription,
  latestStatus: snapshot.latestStatus,
  latestCohortVersionId: snapshot.latestCohortVersionId,
  createdByUserId: snapshot.createdBy?.userId ?? '',
  createdByFirstName: snapshot.createdBy?.firstName ?? '',
  createdByLastName: snapshot.createdBy?.lastName ?? '',
  modifiedByUserId: snapshot.modifiedBy?.userId ?? '',
  modifiedByTransactionDate: snapshot.modifiedBy?.transactionDate ?? '',
  latestSubjectCount: snapshot.latestSubjectCount ?? 0,
  latestDatasetReleaseId: snapshot.latestDatasetReleaseId,
  latestDatasetReleaseDisplayName: snapshot.latestDatasetReleaseDisplayName,
});

interface UseCohortProfileQueryParams {
  projectId: string;
  cohortId: string;
  /** When true, refetches every 5s while the cohort is processing. */
  shouldPoll?: boolean;
  /** Force-disable the query (default: enabled when both ids are set). */
  enabled?: boolean;
}

/**
 * Canonical TanStack read for the cohort profile endpoint
 * (`/cohorts/<projectId>/cohort-profile/<cohortId>`).
 *
 * Keyed on `cohortId` — same cache entry across versions. Save/Process/Revert
 * mutations invalidate this key explicitly. If you need cache freshness
 * keyed on a specific cohort version (e.g. the AV editor's processing
 * watcher), use the version-keyed `useCohortProfileQuery` in
 * `feature-analytical-variables/src/lib/api/` — kept separate by design
 * because its cache contract is different.
 *
 * Replaces the RTK `useGetCohortQuery` endpoint from `cohortAPISlice` for
 * any consumer that needs the projected `CohortProfileView` shape.
 */
export const useCohortProfileQuery = ({
  projectId,
  cohortId,
  shouldPoll,
  enabled,
}: UseCohortProfileQueryParams) => {
  return useQuery<CohortProfileView>({
    queryKey: cohortQueryKeys.profile(cohortId),
    queryFn: async () => {
      const raw = await authFetch<unknown>(
        `/${COHORTS_PATH}/${projectId}/cohort-profile/${cohortId}`
      );
      const envelope = parseOrFallback(objectResponse(cohortSchema), raw, 'useCohortProfileQuery');
      const parsed = envelope.data as CohortFromApi;
      return {
        // This endpoint sends ``cohortProfileId``, not ``cohortId`` — prefer it
        // so the view's id is actually populated (was silently undefined). (OR-6177)
        cohortId: parsed.cohortProfileId ?? parsed.cohortId ?? '',
        cohortName: parsed.cohortName,
        cohortDescription: parsed.cohortDescription,
        latestStatus: parsed.latestStatus as CohortDefinitionStatus,
        // Wire field is a tolerant string; the BE only emits the four AssetStatus
        // labels today, so narrow for consumers. A future unknown value would
        // still render — it just wouldn't type-narrow.
        assetStatus: (parsed.assetStatus ?? null) as AssetStatus | null,
        latestCohortVersionId: parsed.latestCohortVersionId,
        createdByUserId: parsed.createdBy?.userId ?? '',
        createdByFirstName: parsed.createdBy?.firstName ?? '',
        createdByLastName: parsed.createdBy?.lastName ?? '',
        modifiedByUserId: parsed.modifiedBy?.userId ?? '',
        modifiedByTransactionDate: parsed.modifiedBy?.transactionDate ?? '',
        latestSubjectCount: parsed.latestSubjectCount,
        latestDatasetReleaseId: parsed.latestDatasetReleaseId,
        latestDatasetReleaseDisplayName: parsed.latestDatasetReleaseDisplayName,
      };
    },
    enabled: enabled !== false && !!projectId && !!cohortId,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    refetchInterval: shouldPoll ? 5000 : false,
  });
};
